import type { ComponentProps, ReactNode } from 'react';

import Text from '@/components/ui/text';
import { cn } from '@/lib/utils';

type ListItemProps = Omit<ComponentProps<'div'>, 'title'> & {
  title: ReactNode;
  /** A string renders as muted secondary text; a node renders as given. */
  description?: ReactNode;
  /** Right-aligned content such as badges, status or row actions. */
  trailing?: ReactNode;
};

/** The mobile row of a resource list: a title over a description, with trailing content. */
function ListItem({
  title,
  description,
  trailing,
  className,
  ...props
}: ListItemProps) {
  return (
    <div
      data-slot="list-item"
      className={cn(
        'flex flex-row items-center justify-between gap-5 sm:gap-4',
        className
      )}
      {...props}
    >
      <div className="flex min-w-0 flex-col gap-2 sm:gap-1">
        <Text weight="medium" wordBreak="words">
          {title}
        </Text>
        {description ? (
          <div data-slot="list-item-description">
            {typeof description === 'string' ? (
              <Text size="sm" variant="muted" wordBreak="words">
                {description}
              </Text>
            ) : (
              description
            )}
          </div>
        ) : null}
      </div>
      {trailing ? (
        <div
          data-slot="list-item-trailing"
          className="flex shrink-0 flex-row items-center gap-3 sm:gap-2"
        >
          {trailing}
        </div>
      ) : null}
    </div>
  );
}

export { ListItem };
